import {
  AIInsightItem,
  AIInsightsRequestParams,
} from 'Interfaces/ProductWikiInterfaces/aiInsights.types';
import {
  CompetitorsAndaApiResponse,
  CompetitorsAndaRequestParams,
  CompetitorsApiResponse,
  CompetitorsMarketChartsApiResponse,
  CompetitorsMarketChartsRequestParams,
  CompetitorsOverviewApiResponse,
  CompetitorsOverviewRequestParams,
  CompetitorsRequestParams,
} from 'Interfaces/ProductWikiInterfaces/competitors.types';
import {
  COTDistributionApiResponse,
  COTDistributionRequestParams,
} from 'Interfaces/ProductWikiInterfaces/cotDistribution.types';
import {
  MarketLandscapeApiResponse,
  MarketLandscapeRequestParams,
} from 'Interfaces/ProductWikiInterfaces/marketLandscape.types';
import {
  LabelerInformationApiResponse,
  LabelerInformationRequestParams,
} from 'Interfaces/ProductWikiInterfaces/labelerInformation.types';
import {
  NADWaiversApiResponse,
  NADWaiversRequestParams,
} from 'Interfaces/ProductWikiInterfaces/nadWaivers.types';
import {
  DrugEstablishmentsApiResponse,
  DrugEstablishmentsRequestParams,
} from 'Interfaces/ProductWikiInterfaces/drugEstablishments.types';
import {
  TaaDesignatedCountriesApiResponse,
  TaaDesignatedCountriesRequestParams,
} from 'Interfaces/ProductWikiInterfaces/taaDesignatedCountries.types';
import {
  LOEStatus,
  MedicalSupplyCompaniesData,
  OpportunityAtAGlanceRequestParams,
  OpportunityMedicalSupplyRequestParams,
  OpportunityOverview,
  OpportunityTile,
  SparklineData,
} from 'Interfaces/ProductWikiInterfaces/opportunityAtAGlance.types';
import {
  PricingApiResponse,
  PricingRequestParams,
} from 'Interfaces/ProductWikiInterfaces/pricing.types';
import { RegulatoryApiResponse } from 'Interfaces/ProductWikiInterfaces/regulatory.types';
import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { productWikiServices } from 'services/productWikiServices';

/**
 * Product Analysis Store
 * Manages state for Opportunity At a Glance data
 */
export class ProductAnalysisStore {
  // Opportunity At a Glance State (legacy - for backward compatibility)
  opportunityOverview: OpportunityOverview | null = null;
  sparklineData: SparklineData | null = null;
  isOpportunityLoading: boolean = false;
  opportunityError: string | null = null;

  // Split Opportunity States - Tiles
  opportunityTiles: OpportunityTile[] = [];
  loeStatus: LOEStatus | null = null;
  isTilesLoading: boolean = false;
  tilesError: string | null = null;

  // Split Opportunity States - Sparklines
  isSparklinesLoading: boolean = false;
  sparklinesError: string | null = null;

  // Split Opportunity States - Medical Supply Companies (bubble chart)
  isMedicalSupplyLoading: boolean = false;
  medicalSupplyError: string | null = null;

  // Market Landscape State
  marketLandscapeData: MarketLandscapeApiResponse | null = null;
  isMarketLandscapeLoading: boolean = false;
  marketLandscapeError: string | null = null;
  // Track if market landscape is being fetched to prevent duplicate calls
  private isMarketLandscapeFetching: boolean = false;
  private marketLandscapeFetchedKey: string | null = null; // Format: "gfc|period|fromDate|toDate"

  // Pricing Intelligence State
  pricingData: PricingApiResponse | null = null;
  isPricingLoading: boolean = false;
  pricingError: string | null = null;

  // Competitors Tab State (legacy - for backward compatibility)
  competitorsData: CompetitorsApiResponse | null = null;
  isCompetitorsLoading: boolean = false;
  competitorsError: string | null = null;
  currentAndaPage: number = 1;

  // Split Competitors States - Overview (barriers, timeline, manufacturers)
  competitorsOverviewData: CompetitorsOverviewApiResponse | null = null;
  isCompetitorsOverviewLoading: boolean = false;
  competitorsOverviewError: string | null = null;

  // Split Competitors States - ANDA Applications (paginated)
  competitorsAndaData: CompetitorsAndaApiResponse | null = null;
  isCompetitorsAndaLoading: boolean = false;
  competitorsAndaError: string | null = null;

  // Split Competitors States - Market Charts (sales trend, market share)
  competitorsMarketChartsData: CompetitorsMarketChartsApiResponse | null = null;
  isCompetitorsMarketChartsLoading: boolean = false;
  competitorsMarketChartsError: string | null = null;

  // Regulatory Tab State
  regulatoryData: RegulatoryApiResponse | null = null;
  isRegulatoryLoading: boolean = false;
  regulatoryError: string | null = null;

  // Manufacturer Tab - NAD Waivers State
  nadWaiversData: NADWaiversApiResponse | null = null;
  isNadWaiversLoading: boolean = false;
  nadWaiversError: string | null = null;

  // Manufacturer Tab - Labeler Information State
  labelerInformationData: LabelerInformationApiResponse | null = null;
  isLabelerInformationLoading: boolean = false;
  labelerInformationError: string | null = null;

  // Manufacturer Tab - TAA Designated Countries (modal)
  taaDesignatedCountriesData: TaaDesignatedCountriesApiResponse | null = null;
  isTaaDesignatedCountriesLoading: boolean = false;
  taaDesignatedCountriesError: string | null = null;

  // Manufacturer Tab - Drug Establishment Registration (modal)
  drugEstablishmentsData: DrugEstablishmentsApiResponse | null = null;
  isDrugEstablishmentsLoading: boolean = false;
  drugEstablishmentsError: string | null = null;

  // COT Distribution State (for pie chart)
  cotDistributionData: COTDistributionApiResponse | null = null;
  isCOTDistributionLoading: boolean = false;
  cotDistributionError: string | null = null;

  // Medical Supply Companies State (for bubble chart)
  medicalSupplyCompaniesData: MedicalSupplyCompaniesData | null = null;

  // AI Insights State
  aiInsights: AIInsightItem[] | null = null;
  isAIInsightsLoading: boolean = false;
  aiInsightsError: string | null = null;
  // Track if AI insights are being fetched to prevent duplicate calls
  private isAIInsightsFetching: boolean = false;
  private aiInsightsFetchedGfc: string | null = null;

  // Current context
  currentGfc: string | null = null;
  currentPeriod: '3 months' | '6 months' | '12 months' | 'custom' = '3 months';
  currentFromDate: string | null = null;
  currentToDate: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Fetch Opportunity At a Glance data from API
   * @param params - Request parameters (gfc, period, optional dates)
   * @param signal - AbortSignal for canceling requests
   */
  fetchOpportunityAtAGlance = async (
    params: OpportunityAtAGlanceRequestParams,
    signal?: AbortSignal
  ) => {
    // Check if signal is already aborted before starting
    if (signal?.aborted) {
      return;
    }

    this.isOpportunityLoading = true;
    this.opportunityError = null;
    this.currentGfc = params.gfc;
    this.currentPeriod = params.period;
    this.currentFromDate = params.from_date || null;
    this.currentToDate = params.to_date || null;

    try {
      const response = await productWikiServices.getOpportunityAtAGlance(params, signal);

      runInAction(() => {
        if (response.data) {
          if (response.data.error) {
            this.opportunityError = response.data.error;
            this.opportunityOverview = null;
            this.sparklineData = null;
            this.medicalSupplyCompaniesData = null;
          } else {
            this.opportunityOverview = response.data.overview || null;
            this.sparklineData = response.data.sparklines || null;
            this.medicalSupplyCompaniesData = response.data.medicalSupplyCompanies || null;
          }
          this.isOpportunityLoading = false;
        }
      });
    } catch (error: any) {
      // Don't update state or show error if request was aborted
      if (error instanceof Error && (error.name === 'CanceledError' || signal?.aborted)) {
        runInAction(() => {
          this.isOpportunityLoading = false;
        });
        return;
      }

      runInAction(() => {
        this.opportunityError =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to fetch opportunity data';
        this.opportunityOverview = null;
        this.sparklineData = null;
        this.medicalSupplyCompaniesData = null;
        this.isOpportunityLoading = false;

        // Show error toast
        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            'Failed to fetch opportunity data. Please try again.',
          {
            position: 'top-right',
            autoClose: 5000,
          }
        );
      });
    }
  };

  /**
   * Update period and fetch data using split endpoints
   * @param period - Period option
   * @param fromDate - Optional from date for custom period
   * @param toDate - Optional to date for custom period
   */
  updatePeriodAndFetch = async (
    period: '3 months' | '6 months' | '12 months' | 'custom',
    fromDate?: string,
    toDate?: string
  ) => {
    if (!this.currentGfc) {
      return;
    }

    const params: OpportunityAtAGlanceRequestParams = {
      gfc: this.currentGfc,
      period,
    };

    if (period === 'custom' && fromDate && toDate) {
      params.from_date = fromDate;
      params.to_date = toDate;
    }

    // Fetch all opportunity data using split endpoints in parallel
    // Note: Market landscape data is fetched by useMarketLandscapeTab hook
    await Promise.all([
      this.fetchOpportunityTiles(params),
      this.fetchOpportunitySparklines(params),
      this.fetchMedicalSupplyCompanies({
        gfc: this.currentGfc,
        period,
        from_date: fromDate,
        to_date: toDate,
      }),
      this.fetchCOTDistribution({
        gfc: this.currentGfc,
        period,
        from_date: fromDate,
        to_date: toDate,
      }),
    ]);
  };

  /**
   * Clear opportunity data and reset state
   */
  clearOpportunityData = () => {
    // Clear legacy state
    this.opportunityOverview = null;
    this.sparklineData = null;
    this.medicalSupplyCompaniesData = null;
    this.opportunityError = null;
    // Clear split state
    this.opportunityTiles = [];
    this.loeStatus = null;
    this.tilesError = null;
    this.sparklinesError = null;
    this.medicalSupplyError = null;
    // Reset context
    this.currentGfc = null;
    this.currentPeriod = '3 months';
    this.currentFromDate = null;
    this.currentToDate = null;
  };

  /**
   * Reset loading state
   */
  resetLoadingState = () => {
    this.isOpportunityLoading = false;
    this.isTilesLoading = false;
    this.isSparklinesLoading = false;
    this.isMedicalSupplyLoading = false;
  };

  /**
   * Fetch Opportunity Tiles data (split endpoint)
   * Returns tiles (Gov TAM, Gov Volume, Price Band, HHI, Contract Types) and LOE status
   * @param params - Request parameters (gfc, period, optional dates)
   * @param signal - AbortSignal for canceling requests
   */
  fetchOpportunityTiles = async (
    params: OpportunityAtAGlanceRequestParams,
    signal?: AbortSignal
  ) => {
    if (signal?.aborted) {
      return;
    }

    this.isTilesLoading = true;
    this.tilesError = null;
    this.currentGfc = params.gfc;
    this.currentPeriod = params.period;
    this.currentFromDate = params.from_date || null;
    this.currentToDate = params.to_date || null;

    try {
      const response = await productWikiServices.getOpportunityTiles(params, signal);

      runInAction(() => {
        if (response.data) {
          if (response.data.error) {
            this.tilesError = response.data.error;
            this.opportunityTiles = [];
            this.loeStatus = null;
          } else {
            this.opportunityTiles = response.data.tiles || [];
            this.loeStatus = response.data.loeStatus || null;
            // Also update legacy opportunityOverview for backward compatibility
            this.opportunityOverview = {
              tiles: response.data.tiles || [],
              loeStatus: response.data.loeStatus || {
                status: '',
                description: '',
                badgeType: 'pre-loe',
              },
            };
          }
          this.isTilesLoading = false;
        }
      });
    } catch (error: any) {
      if (error instanceof Error && (error.name === 'CanceledError' || signal?.aborted)) {
        runInAction(() => {
          this.isTilesLoading = false;
        });
        return;
      }

      runInAction(() => {
        this.tilesError =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to fetch opportunity tiles data';
        this.opportunityTiles = [];
        this.loeStatus = null;
        this.isTilesLoading = false;

        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            'Failed to fetch opportunity tiles data. Please try again.',
          {
            position: 'top-right',
            autoClose: 5000,
          }
        );
      });
    }
  };

  /**
   * Fetch Opportunity Sparklines data (split endpoint)
   * Returns sales and volume trend arrays for sparkline charts
   * @param params - Request parameters (gfc, period, optional dates)
   * @param signal - AbortSignal for canceling requests
   */
  fetchOpportunitySparklines = async (
    params: OpportunityAtAGlanceRequestParams,
    signal?: AbortSignal
  ) => {
    if (signal?.aborted) {
      return;
    }

    this.isSparklinesLoading = true;
    this.sparklinesError = null;

    try {
      const response = await productWikiServices.getOpportunitySparklines(params, signal);

      runInAction(() => {
        if (response.data) {
          if (response.data.error) {
            this.sparklinesError = response.data.error;
            this.sparklineData = null;
          } else {
            this.sparklineData = {
              salesTrend: response.data.salesTrend || [],
              volumeTrend: response.data.volumeTrend || [],
              salesChange: response.data.salesChange || 'N/A',
              volumeChange: response.data.volumeChange || 'N/A',
            };
          }
          this.isSparklinesLoading = false;
        }
      });
    } catch (error: any) {
      if (error instanceof Error && (error.name === 'CanceledError' || signal?.aborted)) {
        runInAction(() => {
          this.isSparklinesLoading = false;
        });
        return;
      }

      runInAction(() => {
        this.sparklinesError =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to fetch sparklines data';
        this.sparklineData = null;
        this.isSparklinesLoading = false;

        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            'Failed to fetch sparklines data. Please try again.',
          {
            position: 'top-right',
            autoClose: 5000,
          }
        );
      });
    }
  };

  /**
   * Fetch Medical Supply Companies data (split endpoint)
   * Returns data for bubble chart visualization
   * @param params - Request parameters (gfc, period, optional dates for custom)
   * @param signal - AbortSignal for canceling requests
   */
  fetchMedicalSupplyCompanies = async (
    params: OpportunityMedicalSupplyRequestParams,
    signal?: AbortSignal
  ) => {
    if (signal?.aborted) {
      return;
    }

    this.isMedicalSupplyLoading = true;
    this.medicalSupplyError = null;

    try {
      const response = await productWikiServices.getMedicalSupplyCompanies(params, signal);

      runInAction(() => {
        if (response.data) {
          if (response.data.error) {
            this.medicalSupplyError = response.data.error;
            this.medicalSupplyCompaniesData = null;
          } else {
            this.medicalSupplyCompaniesData = {
              data: response.data.data || [],
              totalVolume: response.data.totalVolume || 0,
              totalRevenue: response.data.totalRevenue || 0,
            };
          }
          this.isMedicalSupplyLoading = false;
        }
      });
    } catch (error: any) {
      if (error instanceof Error && (error.name === 'CanceledError' || signal?.aborted)) {
        runInAction(() => {
          this.isMedicalSupplyLoading = false;
        });
        return;
      }

      runInAction(() => {
        this.medicalSupplyError =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to fetch medical supply companies data';
        this.medicalSupplyCompaniesData = null;
        this.isMedicalSupplyLoading = false;

        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            'Failed to fetch medical supply companies data. Please try again.',
          {
            position: 'top-right',
            autoClose: 5000,
          }
        );
      });
    }
  };

  /**
   * Fetch all opportunity data using split endpoints in parallel
   * This is the new recommended method for loading opportunity data
   * @param params - Request parameters (gfc, period, optional dates)
   * @param signal - AbortSignal for canceling requests
   */
  fetchAllOpportunityData = async (
    params: OpportunityAtAGlanceRequestParams,
    signal?: AbortSignal
  ) => {
    if (signal?.aborted) {
      return;
    }

    // Set loading state for all sections
    this.isOpportunityLoading = true;
    this.currentGfc = params.gfc;
    this.currentPeriod = params.period;
    this.currentFromDate = params.from_date || null;
    this.currentToDate = params.to_date || null;

    // Fetch all data in parallel
    await Promise.all([
      this.fetchOpportunityTiles(params, signal),
      this.fetchOpportunitySparklines(params, signal),
      this.fetchMedicalSupplyCompanies(
        {
          gfc: params.gfc,
          period: params.period,
          from_date: params.from_date,
          to_date: params.to_date,
        },
        signal
      ),
      this.fetchCOTDistribution(
        {
          gfc: params.gfc,
          period: params.period,
          from_date: params.from_date,
          to_date: params.to_date,
        },
        signal
      ),
    ]);

    runInAction(() => {
      this.isOpportunityLoading = false;
    });
  };

  /**
   * Fetch Market Landscape data from API
   * Prevents duplicate calls for the same GFC and period combination
   * @param params - Request parameters (gfc, period, optional from_date, to_date)
   * @param signal - AbortSignal for canceling requests
   */
  fetchMarketLandscape = async (params: MarketLandscapeRequestParams, signal?: AbortSignal) => {
    // Check if signal is already aborted before starting
    if (signal?.aborted) {
      return;
    }

    // Create a unique key for this request (GFC + period + dates)
    const requestKey = `${params.gfc?.trim() || ''}|${params.period || ''}|${params.from_date || ''}|${params.to_date || ''}`;

    // Prevent duplicate calls for the same request
    if (this.isMarketLandscapeFetching && this.marketLandscapeFetchedKey === requestKey) {
      return;
    }

    this.isMarketLandscapeFetching = true;
    this.marketLandscapeFetchedKey = requestKey;
    this.isMarketLandscapeLoading = true;
    this.marketLandscapeError = null;

    try {
      const response = await productWikiServices.getMarketLandscape(params, signal);

      runInAction(() => {
        if (response.data) {
          if (response.data.error) {
            this.marketLandscapeError = response.data.error;
            this.marketLandscapeData = null;
          } else {
            this.marketLandscapeData = response.data;
          }
          this.isMarketLandscapeLoading = false;
          this.isMarketLandscapeFetching = false;
        }
      });
    } catch (error: any) {
      // Don't update state or show error if request was aborted
      if (error instanceof Error && (error.name === 'CanceledError' || signal?.aborted)) {
        runInAction(() => {
          this.isMarketLandscapeLoading = false;
          // Reset fetching flag if aborted
          if (this.marketLandscapeFetchedKey === requestKey) {
            this.isMarketLandscapeFetching = false;
          }
        });
        return;
      }

      runInAction(() => {
        this.marketLandscapeError =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to fetch market landscape data';
        this.marketLandscapeData = null;
        this.isMarketLandscapeLoading = false;
        this.isMarketLandscapeFetching = false;

        // Show error toast
        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            'Failed to fetch market landscape data. Please try again.',
          {
            position: 'top-right',
            autoClose: 5000,
          }
        );
      });
    }
  };

  /**
   * Clear market landscape data and reset state
   */
  clearMarketLandscapeData = () => {
    this.marketLandscapeData = null;
    this.marketLandscapeError = null;
    this.isMarketLandscapeFetching = false;
    this.marketLandscapeFetchedKey = null;
  };

  /**
   * Fetch Pricing Intelligence data from API
   * Returns both monthly and quarterly trend data in a single response.
   * @param params - Request parameters (gfc)
   * @param signal - AbortSignal for canceling requests
   */
  fetchPricingData = async (params: PricingRequestParams, signal?: AbortSignal) => {
    // Check if signal is already aborted before starting
    if (signal?.aborted) {
      return;
    }

    this.isPricingLoading = true;
    this.pricingError = null;

    try {
      const response = await productWikiServices.getPricingData(params, signal);

      runInAction(() => {
        if (response.data) {
          if (response.data.error) {
            this.pricingError = response.data.error;
            this.pricingData = null;
          } else {
            this.pricingData = response.data;
          }
          this.isPricingLoading = false;
        }
      });
    } catch (error: any) {
      // Don't update state or show error if request was aborted
      if (error instanceof Error && (error.name === 'CanceledError' || signal?.aborted)) {
        runInAction(() => {
          this.isPricingLoading = false;
        });
        return;
      }

      runInAction(() => {
        this.pricingError =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to fetch pricing data';
        this.pricingData = null;
        this.isPricingLoading = false;

        // Show error toast
        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            'Failed to fetch pricing data. Please try again.',
          {
            position: 'top-right',
            autoClose: 5000,
          }
        );
      });
    }
  };

  /**
   * Clear pricing data and reset state
   */
  clearPricingData = () => {
    this.pricingData = null;
    this.pricingError = null;
  };

  /**
   * Fetch NAD Waivers data for Manufacturer tab
   * @param params - Request parameters (gfc)
   * @param signal - AbortSignal for canceling requests
   */
  fetchNadWaivers = async (
    params: NADWaiversRequestParams,
    signal?: AbortSignal
  ) => {
    if (signal?.aborted) {
      return;
    }
    this.isNadWaiversLoading = true;
    this.nadWaiversError = null;
    this.currentGfc = params.gfc;
    try {
      const response = await productWikiServices.getNadWaivers(params, signal);
      runInAction(() => {
        if (response.data) {
          this.nadWaiversData = response.data;
          this.nadWaiversError = null;
        }
        this.isNadWaiversLoading = false;
      });
    } catch (error: any) {
      if (
        error instanceof Error &&
        (error.name === 'CanceledError' || signal?.aborted)
      ) {
        runInAction(() => {
          this.isNadWaiversLoading = false;
        });
        return;
      }
      runInAction(() => {
        this.nadWaiversError =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to fetch NAD waiver data';
        this.nadWaiversData = null;
        this.isNadWaiversLoading = false;
        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            'Failed to fetch NAD waiver data. Please try again.',
          { position: 'top-right', autoClose: 5000 }
        );
      });
    }
  };

  /**
   * Clear NAD Waivers data and reset state
   */
  clearNadWaiversData = () => {
    this.nadWaiversData = null;
    this.nadWaiversError = null;
  };

  /**
   * Fetch Labeler Information data for Manufacturer tab
   * @param params - Request parameters (gfc)
   * @param signal - AbortSignal for canceling requests
   */
  fetchLabelerInformation = async (
    params: LabelerInformationRequestParams,
    signal?: AbortSignal
  ) => {
    if (signal?.aborted) {
      return;
    }
    this.isLabelerInformationLoading = true;
    this.labelerInformationError = null;
    this.currentGfc = params.gfc;
    try {
      const response = await productWikiServices.getLabelerInformation(params, signal);
      runInAction(() => {
        if (response.data) {
          this.labelerInformationData = response.data;
          this.labelerInformationError = null;
        }
        this.isLabelerInformationLoading = false;
      });
    } catch (error: any) {
      if (
        error instanceof Error &&
        (error.name === 'CanceledError' || signal?.aborted)
      ) {
        runInAction(() => {
          this.isLabelerInformationLoading = false;
        });
        return;
      }
      runInAction(() => {
        this.labelerInformationError =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to fetch labeler information';
        this.labelerInformationData = null;
        this.isLabelerInformationLoading = false;
        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            'Failed to fetch labeler information. Please try again.',
          { position: 'top-right', autoClose: 5000 }
        );
      });
    }
  };

  /**
   * Clear Labeler Information data and reset state
   */
  clearLabelerInformationData = () => {
    this.labelerInformationData = null;
    this.labelerInformationError = null;
  };

  /**
   * Fetch TAA Designated Countries data for the modal
   * @param params - Optional pagination and search (page, page_size, search)
   * @param signal - AbortSignal for canceling requests
   */
  fetchTaaDesignatedCountries = async (
    params?: TaaDesignatedCountriesRequestParams,
    signal?: AbortSignal
  ) => {
    if (signal?.aborted) {
      return;
    }
    this.isTaaDesignatedCountriesLoading = true;
    this.taaDesignatedCountriesError = null;
    try {
      const response = await productWikiServices.getTaaDesignatedCountries(
        params,
        signal
      );
      runInAction(() => {
        if (response.data) {
          this.taaDesignatedCountriesData = response.data;
          this.taaDesignatedCountriesError = null;
        }
        this.isTaaDesignatedCountriesLoading = false;
      });
    } catch (error: any) {
      if (
        error instanceof Error &&
        (error.name === 'CanceledError' || signal?.aborted)
      ) {
        runInAction(() => {
          this.isTaaDesignatedCountriesLoading = false;
        });
        return;
      }
      runInAction(() => {
        this.taaDesignatedCountriesError =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to fetch TAA designated countries';
        this.taaDesignatedCountriesData = null;
        this.isTaaDesignatedCountriesLoading = false;
        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            'Failed to fetch TAA designated countries. Please try again.',
          { position: 'top-right', autoClose: 5000 }
        );
      });
    }
  };

  /**
   * Clear TAA Designated Countries data and reset state
   */
  clearTaaDesignatedCountriesData = () => {
    this.taaDesignatedCountriesData = null;
    this.taaDesignatedCountriesError = null;
  };

  /**
   * Fetch Drug Establishment Registration data for the Search Establishments modal
   * @param params - Request parameters (gfc)
   * @param signal - AbortSignal for canceling requests
   */
  fetchDrugEstablishments = async (
    params: DrugEstablishmentsRequestParams,
    signal?: AbortSignal
  ) => {
    if (signal?.aborted) {
      return;
    }
    this.isDrugEstablishmentsLoading = true;
    this.drugEstablishmentsError = null;
    try {
      const response = await productWikiServices.getDrugEstablishments(params, signal);
      runInAction(() => {
        if (response.data) {
          this.drugEstablishmentsData = response.data;
          this.drugEstablishmentsError = null;
        }
        this.isDrugEstablishmentsLoading = false;
      });
    } catch (error: any) {
      if (
        error instanceof Error &&
        (error.name === 'CanceledError' || signal?.aborted)
      ) {
        runInAction(() => {
          this.isDrugEstablishmentsLoading = false;
        });
        return;
      }
      runInAction(() => {
        this.drugEstablishmentsError =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to fetch drug establishments';
        this.drugEstablishmentsData = null;
        this.isDrugEstablishmentsLoading = false;
        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            'Failed to fetch drug establishments. Please try again.',
          { position: 'top-right', autoClose: 5000 }
        );
      });
    }
  };

  /**
   * Clear Drug Establishment Registration data and reset state
   */
  clearDrugEstablishmentsData = () => {
    this.drugEstablishmentsData = null;
    this.drugEstablishmentsError = null;
  };

  /**
   * Fetch Competitors Tab data from API
   * @param params - Request parameters (gfc, page, page_size)
   * @param signal - AbortSignal for canceling requests
   */
  fetchCompetitorsData = async (params: CompetitorsRequestParams, signal?: AbortSignal) => {
    this.isCompetitorsLoading = true;
    this.competitorsError = null;
    this.currentAndaPage = params.page || 1;
    // Track which GFC this data belongs to
    this.currentGfc = params.gfc;

    try {
      const response = await productWikiServices.getCompetitorsData(params, signal);

      runInAction(() => {
        if (response.data) {
          if (response.data.error) {
            this.competitorsError = response.data.error;
            this.competitorsData = null;
          } else {
            this.competitorsData = response.data;
          }
          this.isCompetitorsLoading = false;
        }
      });
    } catch (error: any) {
      // Don't update state or show error if request was aborted
      if (error instanceof Error && (error.name === 'CanceledError' || signal?.aborted)) {
        runInAction(() => {
          this.isCompetitorsLoading = false;
        });
        return;
      }

      runInAction(() => {
        this.competitorsError =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to fetch competitors data';
        this.competitorsData = null;
        this.isCompetitorsLoading = false;

        // Show error toast
        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            'Failed to fetch competitors data. Please try again.',
          {
            position: 'top-right',
            autoClose: 5000,
          }
        );
      });
    }
  };

  /**
   * Update ANDA page and fetch data
   * @param gfc - GFC code
   * @param page - Page number
   * @param pageSize - Page size (optional, defaults to 10)
   */
  setAndaPage = async (gfc: string, page: number, pageSize: number = 10) => {
    await this.fetchCompetitorsData({
      gfc,
      page,
      page_size: pageSize,
    });
  };

  /**
   * Clear competitors data and reset state
   */
  clearCompetitorsData = () => {
    // Clear legacy state
    this.competitorsData = null;
    this.competitorsError = null;
    this.currentAndaPage = 1;
    // Clear split state
    this.competitorsOverviewData = null;
    this.competitorsOverviewError = null;
    this.competitorsAndaData = null;
    this.competitorsAndaError = null;
    this.competitorsMarketChartsData = null;
    this.competitorsMarketChartsError = null;
  };

  /**
   * Fetch Competitors Overview data (split endpoint)
   * Returns barriers, timeline, and manufacturers data
   * @param params - Request parameters (gfc)
   * @param signal - AbortSignal for canceling requests
   */
  fetchCompetitorsOverview = async (
    params: CompetitorsOverviewRequestParams,
    signal?: AbortSignal
  ) => {
    if (signal?.aborted) {
      return;
    }

    this.isCompetitorsOverviewLoading = true;
    this.competitorsOverviewError = null;
    this.currentGfc = params.gfc;

    try {
      const response = await productWikiServices.getCompetitorsOverview(params, signal);

      runInAction(() => {
        if (response.data) {
          if (response.data.error) {
            this.competitorsOverviewError = response.data.error;
            this.competitorsOverviewData = null;
          } else {
            this.competitorsOverviewData = response.data;
          }
          this.isCompetitorsOverviewLoading = false;
        }
      });
    } catch (error: any) {
      if (error instanceof Error && (error.name === 'CanceledError' || signal?.aborted)) {
        runInAction(() => {
          this.isCompetitorsOverviewLoading = false;
        });
        return;
      }

      runInAction(() => {
        this.competitorsOverviewError =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to fetch competitors overview data';
        this.competitorsOverviewData = null;
        this.isCompetitorsOverviewLoading = false;

        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            'Failed to fetch competitors overview data. Please try again.',
          {
            position: 'top-right',
            autoClose: 5000,
          }
        );
      });
    }
  };

  /**
   * Fetch Competitors ANDA Applications data (split endpoint with pagination)
   * @param params - Request parameters (gfc, page, page_size)
   * @param signal - AbortSignal for canceling requests
   */
  fetchCompetitorsAnda = async (params: CompetitorsAndaRequestParams, signal?: AbortSignal) => {
    if (signal?.aborted) {
      return;
    }

    this.isCompetitorsAndaLoading = true;
    this.competitorsAndaError = null;
    this.currentAndaPage = params.page || 1;
    this.currentGfc = params.gfc;

    try {
      const response = await productWikiServices.getCompetitorsAnda(params, signal);

      runInAction(() => {
        if (response.data) {
          if (response.data.error) {
            this.competitorsAndaError = response.data.error;
            this.competitorsAndaData = null;
          } else {
            this.competitorsAndaData = response.data;
          }
          this.isCompetitorsAndaLoading = false;
        }
      });
    } catch (error: any) {
      if (error instanceof Error && (error.name === 'CanceledError' || signal?.aborted)) {
        runInAction(() => {
          this.isCompetitorsAndaLoading = false;
        });
        return;
      }

      runInAction(() => {
        this.competitorsAndaError =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to fetch ANDA applications data';
        this.competitorsAndaData = null;
        this.isCompetitorsAndaLoading = false;

        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            'Failed to fetch ANDA applications data. Please try again.',
          {
            position: 'top-right',
            autoClose: 5000,
          }
        );
      });
    }
  };

  /**
   * Update ANDA page and fetch data (split endpoint version)
   * Only refreshes ANDA section, not the entire page. Pass filters to keep server-side filter state.
   * @param gfc - GFC code
   * @param page - Page number
   * @param pageSize - Page size (optional, defaults to 10)
   * @param filters - Optional server-side filter params (search, application_number, etc.)
   */
  setAndaPageSplit = async (
    gfc: string,
    page: number,
    pageSize: number = 10,
    filters?: Omit<CompetitorsAndaRequestParams, 'gfc' | 'page' | 'page_size'>
  ) => {
    await this.fetchCompetitorsAnda({
      gfc,
      page,
      page_size: pageSize,
      ...filters,
    });
  };

  /**
   * Fetch Competitors Market Charts data (split endpoint)
   * Returns market sales trend and market share by volume data
   * @param params - Request parameters (gfc)
   * @param signal - AbortSignal for canceling requests
   */
  fetchCompetitorsMarketCharts = async (
    params: CompetitorsMarketChartsRequestParams,
    signal?: AbortSignal
  ) => {
    if (signal?.aborted) {
      return;
    }

    this.isCompetitorsMarketChartsLoading = true;
    this.competitorsMarketChartsError = null;
    this.currentGfc = params.gfc;

    try {
      const response = await productWikiServices.getCompetitorsMarketCharts(params, signal);

      runInAction(() => {
        if (response.data) {
          if (response.data.error) {
            this.competitorsMarketChartsError = response.data.error;
            this.competitorsMarketChartsData = null;
          } else {
            this.competitorsMarketChartsData = response.data;
          }
          this.isCompetitorsMarketChartsLoading = false;
        }
      });
    } catch (error: any) {
      if (error instanceof Error && (error.name === 'CanceledError' || signal?.aborted)) {
        runInAction(() => {
          this.isCompetitorsMarketChartsLoading = false;
        });
        return;
      }

      runInAction(() => {
        this.competitorsMarketChartsError =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to fetch market charts data';
        this.competitorsMarketChartsData = null;
        this.isCompetitorsMarketChartsLoading = false;

        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            'Failed to fetch market charts data. Please try again.',
          {
            position: 'top-right',
            autoClose: 5000,
          }
        );
      });
    }
  };

  /**
   * Fetch all competitors data using split endpoints in parallel
   * This is the new recommended method for loading competitors data
   * @param gfc - Generic/FDA Code (concept identifier)
   * @param signal - AbortSignal for canceling requests
   */
  fetchAllCompetitorsData = async (gfc: string, signal?: AbortSignal) => {
    if (signal?.aborted) {
      return;
    }

    this.currentGfc = gfc;

    // Fetch all data in parallel
    await Promise.all([
      this.fetchCompetitorsOverview({ gfc }, signal),
      this.fetchCompetitorsAnda({ gfc, page: 1, page_size: 10 }, signal),
      this.fetchCompetitorsMarketCharts({ gfc }, signal),
    ]);
  };

  /**
   * Fetch Regulatory Tab data from API
   * @param params - Request parameters (gfc)
   * @param signal - AbortSignal for canceling requests
   */
  // fetchRegulatoryData = async (params: RegulatoryRequestParams, signal?: AbortSignal) => {
  //   // Check if signal is already aborted before starting
  //   if (signal?.aborted) {
  //     return;
  //   }

  //   this.isRegulatoryLoading = true;
  //   this.regulatoryError = null;

  //   try {
  //     const response = await productWikiServices.getRegulatoryData(params, signal);

  //     runInAction(() => {
  //       if (response.data) {
  //         if (response.data.error) {
  //           this.regulatoryError = response.data.error;
  //           this.regulatoryData = null;
  //         } else {
  //           this.regulatoryData = response.data;
  //         }
  //         this.isRegulatoryLoading = false;
  //       }
  //     });
  //   } catch (error: any) {
  //     // Don't update state or show error if request was aborted
  //     if (error instanceof Error && (error.name === 'CanceledError' || signal?.aborted)) {
  //       runInAction(() => {
  //         this.isRegulatoryLoading = false;
  //       });
  //       return;
  //     }

  //     runInAction(() => {
  //       this.regulatoryError =
  //         error?.response?.data?.message ||
  //         error?.response?.data?.error ||
  //         'Failed to fetch regulatory data';
  //       this.regulatoryData = null;
  //       this.isRegulatoryLoading = false;

  //       // Show error toast
  //       toast.error(
  //         error?.response?.data?.message ||
  //           error?.response?.data?.error ||
  //           'Failed to fetch regulatory data. Please try again.',
  //         {
  //           position: 'top-right',
  //           autoClose: 5000,
  //         }
  //       );
  //     });
  //   }
  // };

  /**
   * Clear regulatory data and reset state
   */
  clearRegulatoryData = () => {
    this.regulatoryData = null;
    this.regulatoryError = null;
  };

  /**
   * Fetch COT Distribution data from API
   * @param params - Request parameters (gfc, period, optional dates)
   * @param signal - AbortSignal for canceling requests
   */
  fetchCOTDistribution = async (params: COTDistributionRequestParams, signal?: AbortSignal) => {
    // Check if signal is already aborted before starting
    if (signal?.aborted) {
      return;
    }

    this.isCOTDistributionLoading = true;
    this.cotDistributionError = null;

    try {
      const response = await productWikiServices.getCOTDistribution(params, signal);

      runInAction(() => {
        if (response.data) {
          if (response.data.error) {
            this.cotDistributionError = response.data.error;
            this.cotDistributionData = null;
          } else {
            this.cotDistributionData = response.data;
          }
          this.isCOTDistributionLoading = false;
        }
      });
    } catch (error: any) {
      // Don't update state or show error if request was aborted
      if (error instanceof Error && (error.name === 'CanceledError' || signal?.aborted)) {
        runInAction(() => {
          this.isCOTDistributionLoading = false;
        });
        return;
      }

      runInAction(() => {
        this.cotDistributionError =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to fetch agency distribution data';
        this.cotDistributionData = null;
        this.isCOTDistributionLoading = false;

        // Show error toast
        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            'Failed to fetch agency distribution data. Please try again.',
          {
            position: 'top-right',
            autoClose: 5000,
          }
        );
      });
    }
  };

  /**
   * Clear COT distribution data and reset state
   */
  clearCOTDistributionData = () => {
    this.cotDistributionData = null;
    this.cotDistributionError = null;
  };

  /**
   * Fetch AI Insights data from API
   * Prevents duplicate calls for the same GFC
   * @param params - Request parameters (insight_type, gfc, period_option, llm_backend)
   * @param signal - AbortSignal for canceling requests
   */
  fetchAIInsights = async (params: AIInsightsRequestParams, signal?: AbortSignal) => {
    // Check if signal is already aborted before starting
    if (signal?.aborted) {
      return;
    }

    // Prevent duplicate calls for the same GFC
    if (this.isAIInsightsFetching && this.aiInsightsFetchedGfc === params.gfc.trim()) {
      return;
    }

    this.isAIInsightsFetching = true;
    this.aiInsightsFetchedGfc = params.gfc.trim();
    this.isAIInsightsLoading = true;
    this.aiInsightsError = null;

    try {
      const response = await productWikiServices.generateAIInsights(params, signal);

      runInAction(() => {
        if (response.data) {
          if (response.data.error) {
            this.aiInsightsError = response.data.error;
            this.aiInsights = null;
          } else if (response.data.success && response.data.insights) {
            this.aiInsights = response.data.insights;
          } else {
            this.aiInsights = null;
            this.aiInsightsError = response.data.message || 'Failed to load insights';
          }
          this.isAIInsightsLoading = false;
          this.isAIInsightsFetching = false;
        }
      });
    } catch (error: any) {
      // Don't update state or show error if request was aborted
      if (error instanceof Error && (error.name === 'CanceledError' || signal?.aborted)) {
        runInAction(() => {
          this.isAIInsightsLoading = false;
          // Reset fetching flag if aborted
          if (this.aiInsightsFetchedGfc === params.gfc.trim()) {
            this.isAIInsightsFetching = false;
          }
        });
        return;
      }

      runInAction(() => {
        this.aiInsightsError =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to fetch AI insights';
        this.aiInsights = null;
        this.isAIInsightsLoading = false;
        this.isAIInsightsFetching = false;

        // Show error toast
        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            'Failed to fetch AI insights. Please try again.',
          {
            position: 'top-right',
            autoClose: 5000,
          }
        );
      });
    }
  };

  /**
   * Clear AI insights data and reset state
   */
  clearAIInsightsData = () => {
    this.aiInsights = null;
    this.aiInsightsError = null;
    this.isAIInsightsFetching = false;
    this.aiInsightsFetchedGfc = null;
  };
}

// Create singleton instance
export const productAnalysisStore = new ProductAnalysisStore();
